/** Profile identity sync — display name, bio and avatar (photo/GIF/video) over E2EE.
 * SPEC §5.1 (account), §9.4 (seal budget), §14 (media encryption), §21 (schemas).
 * Profile payloads travel as CONTENT_TYPE_PROFILE inside the conversation ratchet;
 * avatar bytes reuse the encrypted-chunk attachment pipeline (SEC-MED-001/002). */

import { z } from "zod";
import {
  MAX_SEAL_PLAINTEXT,
  encryptAttachmentFile,
  resolveAttachmentEndpoints,
  uploadChunksDual,
  downloadChunksDual,
  decryptAttachmentChunk,
  type AttachmentManifest,
} from "./attachments";

export const CONTENT_TYPE_PROFILE = 12;

export const MAX_PROFILE_NAME_CHARS = 64;
export const MAX_ABOUT_CHARS = 280;

/** Wire/storage caps for avatar media (SPEC resource budget discipline). */
export const PROFILE_IMAGE_INPUT_MAX_BYTES = 15 * 1024 * 1024; // raw photo before client-side downscale
export const PROFILE_MEDIA_MAX_BYTES = 3 * 1024 * 1024; // gif / video avatar cap
export const PROFILE_AVATAR_PIXELS = 256; // processed square
export const PROFILE_VIDEO_MAX_SECONDS = 6;

export type ProfileMediaKind = "image" | "video" | "gif";

/** Local, decrypted avatar representation (never sent on the wire in this form). */
export interface ProfileMedia {
  kind: ProfileMediaKind;
  mime: string;
  dataB64: string;
  updatedAt: number;
}

const PROFILE_IMAGE_MIMES = new Set(["image/jpeg", "image/png", "image/webp"]);
const PROFILE_GIF_MIMES = new Set(["image/gif"]);
const PROFILE_VIDEO_MIMES = new Set(["video/mp4", "video/webm"]);
export const ALLOWED_PROFILE_WIRE_MIMES = new Set([
  ...PROFILE_IMAGE_MIMES,
  ...PROFILE_GIF_MIMES,
  ...PROFILE_VIDEO_MIMES,
]);

/** Classify a picked file into an avatar kind; null when unsupported (SEC-MED-003 allowlist). */
export function detectProfileMediaKind(
  mediaType: string,
  fileName: string,
): ProfileMediaKind | null {
  const mime = (mediaType || "").toLowerCase().split(";")[0]!.trim();
  if (PROFILE_GIF_MIMES.has(mime)) return "gif";
  if (PROFILE_IMAGE_MIMES.has(mime)) return "image";
  if (PROFILE_VIDEO_MIMES.has(mime)) return "video";
  const ext = fileName.toLowerCase().split(".").pop() ?? "";
  if (ext === "gif") return "gif";
  if (ext === "jpg" || ext === "jpeg" || ext === "png" || ext === "webp") return "image";
  if (ext === "mp4" || ext === "webm") return "video";
  return null;
}

export type ProfileMediaRejectReason =
  | "empty"
  | "unsupported"
  | "too_large";

/** Pure policy check applied before any processing of a picked avatar file. */
export function validateProfileMediaFile(input: {
  fileName: string;
  mediaType: string;
  size: number;
}): { ok: true; kind: ProfileMediaKind } | { ok: false; reason: ProfileMediaRejectReason; message: string } {
  if (!input.fileName || input.size <= 0) {
    return { ok: false, reason: "empty", message: "Empty file rejected" };
  }
  const kind = detectProfileMediaKind(input.mediaType, input.fileName);
  if (!kind) {
    return {
      ok: false,
      reason: "unsupported",
      message: "Use a JPG, PNG, WebP, GIF, MP4 or WebM file",
    };
  }
  const max = kind === "image" ? PROFILE_IMAGE_INPUT_MAX_BYTES : PROFILE_MEDIA_MAX_BYTES;
  if (input.size > max) {
    return {
      ok: false,
      reason: "too_large",
      message:
        kind === "image"
          ? "Photo exceeds 15 MiB"
          : `${kind === "gif" ? "GIF" : "Video"} exceeds 3 MiB`,
    };
  }
  return { ok: true, kind };
}

/** Avatar media as embedded in a ProfileWirePayload — ciphertext chunks + per-conversation key. */
export interface ProfileAvatarWire {
  kind: ProfileMediaKind;
  mime: string;
  manifest: AttachmentManifest;
  inlineChunksB64?: string[];
}

export interface ProfileWirePayload {
  op: "profile";
  v: 1;
  displayName: string;
  about: string;
  updatedAt: number;
  avatar?: ProfileAvatarWire | null;
}

function b64(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

function unb64(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function asBuf(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

const ChunkRefSchema = z.object({
  store: z.enum(["a", "b"]),
  objectId: z.string().length(64),
  size: z.number().int().min(1),
  ciphertextHash: z.string().length(64),
});

const AvatarManifestSchema = z.object({
  v: z.literal(1),
  manifestId: z.string().min(8).max(64),
  mediaType: z.string().min(3).max(128),
  fileName: z.string().max(64),
  totalSize: z.number().int().min(1).max(PROFILE_MEDIA_MAX_BYTES),
  chunks: z.array(ChunkRefSchema).min(1).max(16),
  fileKeyB64: z.string().min(16).max(64),
  fileNonceB64: z.string().min(8).max(32),
});

const ProfileAvatarWireSchema = z.object({
  kind: z.enum(["image", "video", "gif"]),
  mime: z.string().refine((m) => ALLOWED_PROFILE_WIRE_MIMES.has(m.toLowerCase()), "blocked mime"),
  manifest: AvatarManifestSchema,
  inlineChunksB64: z.array(z.string().min(1).max(4096)).max(16).optional(),
});

export const ProfileWirePayloadSchema = z
  .object({
    op: z.literal("profile"),
    v: z.literal(1),
    displayName: z.string().min(1).max(MAX_PROFILE_NAME_CHARS),
    about: z.string().max(MAX_ABOUT_CHARS),
    updatedAt: z.number().int().positive(),
    avatar: ProfileAvatarWireSchema.nullable().optional(),
  })
  .strict();

/** Serialize a profile update, enforcing the ratchet seal budget (SPEC §9.5). */
export function encodeProfilePayload(payload: ProfileWirePayload): string {
  ProfileWirePayloadSchema.parse(payload);
  const json = JSON.stringify(payload);
  if (new TextEncoder().encode(json).length > MAX_SEAL_PLAINTEXT) {
    throw new Error("Profile payload exceeds seal padding budget (SPEC §9.5)");
  }
  return json;
}

/** Validate an untrusted inbound profile payload (SPEC §21.4 — reject unknown critical fields). */
export function decodeProfilePayload(json: string): ProfileWirePayload {
  return ProfileWirePayloadSchema.parse(JSON.parse(json)) as ProfileWirePayload;
}

/**
 * Encrypt an avatar blob for transport: ciphertext chunks via the dual object store
 * (SEC-MED-002), with inline fallback when everything fits one seal bucket (SEC-MED-001).
 * The original file name is discarded — only "avatar" travels on the wire.
 */
export async function buildProfileAvatarWire(input: {
  blob: Blob;
  mime: string;
  kind: ProfileMediaKind;
  endpoints?: { a: string; b: string };
}): Promise<{ avatar: ProfileAvatarWire; delivery: "inline" | "object-store" }> {
  const { manifest, chunks } = await encryptAttachmentFile(input.blob, "avatar", input.mime);
  const endpoints = resolveAttachmentEndpoints(input.endpoints);
  let uploaded = false;
  try {
    await uploadChunksDual(chunks, manifest.chunks, endpoints);
    uploaded = true;
  } catch {
    /* best-effort — store may be offline in lab */
  }
  const withInline: ProfileAvatarWire = {
    kind: input.kind,
    mime: input.mime,
    manifest,
    inlineChunksB64: chunks.map(b64),
  };
  if (new TextEncoder().encode(JSON.stringify(withInline)).length <= MAX_SEAL_PLAINTEXT) {
    return { avatar: withInline, delivery: "inline" };
  }
  if (!uploaded) {
    throw new Error("Avatar too large for inline delivery and media store unavailable");
  }
  const manifestOnly: ProfileAvatarWire = {
    kind: input.kind,
    mime: input.mime,
    manifest,
  };
  return { avatar: manifestOnly, delivery: "object-store" };
}

/** Decrypt an inbound avatar back to a Blob (chunks fetched from store when not inline). */
export async function decryptProfileAvatar(avatar: ProfileAvatarWire): Promise<Blob> {
  let packedChunks: Uint8Array[];
  if (avatar.inlineChunksB64?.length) {
    packedChunks = avatar.inlineChunksB64.map(unb64);
  } else {
    packedChunks = await downloadChunksDual(avatar.manifest.chunks);
  }
  const parts: Uint8Array[] = [];
  for (let i = 0; i < packedChunks.length; i++) {
    parts.push(
      await decryptAttachmentChunk(
        packedChunks[i]!,
        avatar.manifest.fileKeyB64,
        i,
        avatar.manifest.totalSize,
      ),
    );
  }
  return new Blob(parts as BlobPart[], { type: avatar.mime });
}

/** Convenience for callers holding raw bytes (tests, local media pipeline). */
export function bytesToProfileBlob(bytes: Uint8Array, mime: string): Blob {
  return new Blob([asBuf(bytes)], { type: mime });
}
